import { Button } from "@/v2-components/ui/button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/v2-components/ui/dialog";
import React, { useMemo, useState } from "react";
import { SelectCountry } from "@/components/SelectCountry";
import { SelectLanguage } from "@/components/SelectLanguage";
import { getDefaultLanguageCode, LanguageCode } from "@/utils/LocaleUtil";
import { useGeo } from "@/hooks/useGeo";
import { cn } from "@/libs/utils";
import ConfirmCountry from "@/components/ConfirmCountry";

interface DialogTexts {
  header: string;
  // 비배송국 결제 차단으로 열렸을 때의 타이틀 (배송 불가 사유 안내).
  nonShippableHeader: string;
  option1: string;
  option2: string;
  cancel: string;
  save: string;
}

const TRANSLATIONS: Record<LanguageCode, DialogTexts> = {
  en: {
    header: "Change shipping country/region and language",
    nonShippableHeader:
      "Sorry, we don't ship to the selected country. Please choose a different one.",
    option1: "Shipping country/region",
    option2: "Language",
    cancel: "Cancel",
    save: "Save",
  },
  ko: {
    header: "배송 국가/지역 및 언어 변경",
    nonShippableHeader:
      "죄송합니다. 현재 국가로는 배송이 불가합니다. 다른 국가를 선택해 주세요.",
    option1: "배송 국가/지역",
    option2: "언어",
    cancel: "취소",
    save: "저장",
  },
  ja: {
    header: "配送先の国/地域と言語を変更する",
    nonShippableHeader:
      "申し訳ございません。選択された国へは配送できません。別の国を選択してください。",
    option1: "配送先の国/地域",
    option2: "言語",
    cancel: "キャンセル",
    save: "保存",
  },
  zh: {
    header: "更改送货国家/地区和语言",
    nonShippableHeader:
      "抱歉，无法配送至您所选的国家/地区，请选择其他国家/地区。",
    option1: "送货国家/地区",
    option2: "语言",
    cancel: "取消",
    save: "保存",
  },
  fr: {
    header: "Modifier le pays/région de livraison et la langue",
    nonShippableHeader:
      "Désolé, nous ne livrons pas dans le pays sélectionné. Veuillez en choisir un autre.",
    option1: "Pays/région de livraison",
    option2: "Langue",
    cancel: "Annuler",
    save: "Enregistrer",
  },
  de: {
    header: "Versandland/-region und Sprache ändern",
    nonShippableHeader:
      "Leider liefern wir nicht in das ausgewählte Land. Bitte wählen Sie ein anderes.",
    option1: "Versandland/-region",
    option2: "Sprache",
    cancel: "Abbrechen",
    save: "Speichern",
  },
  es: {
    header: "Cambiar país/región e idioma de envío",
    nonShippableHeader:
      "Lo sentimos, no realizamos envíos al país seleccionado. Elige otro país.",
    option1: "País/región de envío",
    option2: "Idioma",
    cancel: "Cancelar",
    save: "Guardar",
  },
};

export default function SelectLocaleDialog({
  onOpenChange,
  countryCode,
  languageCode = "en",
  onSave,
  nonShippable = false,
}: {
  countryCode: string;
  languageCode?: LanguageCode;
  onOpenChange: (open: boolean) => void;
  onSave: (countryCode: string, languageCode: LanguageCode) => void;
  // true 면 비배송국 차단 안내 타이틀로 표시 (결제 차단 → 국가 변경 유도).
  nonShippable?: boolean;
}) {
  const [state, setState] = useState<{
    countryCode: string;
    languageCode: LanguageCode;
  }>({ countryCode, languageCode });
  const { data } = useGeo();
  const [showAlert, setShowAlert] = useState(false);
  const canShowAlert =
    !!data && data.shippingSupported && state.countryCode !== data.countryCode;

  const updated = useMemo(() => {
    return (
      state.countryCode !== countryCode || state.languageCode !== languageCode
    );
  }, [state, countryCode, languageCode]);

  const T = TRANSLATIONS[state.languageCode] ?? TRANSLATIONS.en;

  return (
    <>
      {!showAlert && <Overlay />}
      {showAlert && (
        <ConfirmCountry
          languageCode={state.languageCode}
          onCancel={() => {
            setShowAlert(false);
          }}
          onConfirm={() => {
            onSave(state.countryCode, state.languageCode);
            onOpenChange(false);
          }}
          onClose={() => {
            onOpenChange(false);
          }}
        />
      )}
      {/* modal 일때 SelectCountry 에서 스크롤이 안되어서 modal={false} 로 하고 Overlay 를 추가함 */}
      <Dialog modal={false} open={!showAlert}>
        <DialogContent className="z-[100] flex max-w-[520px] flex-col p-8">
          <DialogHeader className="flex items-start">
            <DialogTitle className="text-2xl">
              {nonShippable ? T.nonShippableHeader : T.header}
            </DialogTitle>
            <div
              onClick={() => {
                onOpenChange(false);
              }}
              className="w-8.5 cursor-pointer"
            >
              <DialogClose className="size-6" />
            </div>
          </DialogHeader>
          <DialogDescription className="flex-1" asChild>
            <div className="flex flex-col gap-y-3">
              <div className="flex flex-col gap-y-3">
                <Label>{T.option1}</Label>
                <SelectCountry
                  countryCode={state.countryCode}
                  setCountryCode={(countryCode) => {
                    const languageCode = getDefaultLanguageCode(countryCode);
                    setState((prev) => ({
                      ...prev,
                      languageCode,
                      countryCode,
                    }));
                  }}
                />
              </div>
              <div className="flex flex-col gap-y-3">
                <Label>{T.option2}</Label>
                <SelectLanguage
                  countryCode={state.countryCode}
                  languageCode={state.languageCode}
                  setLanguageCode={(languageCode) => {
                    setState((prev) => ({ ...prev, languageCode }));
                  }}
                />
              </div>
            </div>
          </DialogDescription>
          <DialogFooter className="flex flex-wrap items-center justify-end gap-2">
            <Button
              onClick={() => onOpenChange(false)}
              variant="blue-outline"
              shape="pill"
              size="sm"
              className="!px-6"
            >
              {T.cancel}
            </Button>
            <Button
              onClick={() => {
                if (canShowAlert) {
                  setShowAlert(true);
                  return;
                }
                onSave(state.countryCode, state.languageCode);
                onOpenChange(false);
              }}
              disabled={!updated}
              variant="blue-fill"
              shape="pill"
              size="sm"
              className="!px-6"
            >
              {T.save}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

const Label = ({
  className,
  children,
}: React.PropsWithChildren<{
  className?: string;
}>) => (
  <h4 className={cn("text-xs font-500 text-coolgray-60", className)}>
    {children}
  </h4>
);

const Overlay = ({
  className,
  children,
}: React.PropsWithChildren<{
  className?: string;
}>) => (
  <div
    data-state="open"
    className={cn(
      "fixed inset-0 z-[100] bg-black/70 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
      className,
    )}
    data-aria-hidden="true"
    aria-hidden="true"
  >
    {children}
  </div>
);
